import { createRouter, createRoute, createRootRoute, Outlet, notFound, redirect, Link } from "@tanstack/react-router";
import { usePlatform } from "./hooks/usePlatform.js";
import { BottomBar } from "./components/BottomBar.js";
import { App } from "./App";
import { AdminPage } from "./components/AdminPage";
import { ProfilePage } from "./components/ProfilePage";
import { MyRoomsPage } from "./pages/MyRoomsPage";
import { RoomEditorPage } from "./pages/RoomEditorPage";
import { PremiumPage } from "./pages/PremiumPage";
import { SourcePage } from "./pages/SourcePage";
import { SourceEditorPage } from "./pages/SourceEditorPage";
import { DigestPage } from "./pages/DigestPage";
import { FiltersPage } from "./pages/FiltersPage";
import { WebhooksPage } from "./pages/WebhooksPage";
import { RulesPage } from "./pages/RulesPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { TeamPage } from "./pages/TeamPage";
import { AdminFeedsPage } from "./pages/AdminFeedsPage";
import { AdminRoomsPage } from "./pages/AdminRoomsPage";
import { AdminRoomEditorPage } from "./pages/AdminRoomEditorPage";
import { AdminFeedbackPage } from "./pages/AdminFeedbackPage";
import { AdminIssuesPage } from "./pages/AdminIssuesPage";
import { AdminCatalogImportPage } from "./pages/AdminCatalogImportPage";
import { AdminCatalogReviewPage } from "./pages/AdminCatalogReviewPage";
import { AdminSiteMapPage } from "./pages/AdminSiteMapPage";
import { FeedEditorPage } from "./pages/FeedEditorPage";
import styles from "./styles/NotFound.module.css";

function RootLayout() {
  const { isMobile } = usePlatform();
  return (
    <>
      <Outlet />
      {isMobile && <BottomBar />}
    </>
  );
}

function desktopOnly() {
  if (window.matchMedia("(max-width: 899px)").matches) {
    throw redirect({ to: "/" });
  }
}

function NotFound() {
  return (
    <div className={styles.page}>
      <span className={styles.code}>404</span>
      <span className={styles.message}>Page not found</span>
      <Link to="/" className={styles.link}>Go home</Link>
    </div>
  );
}

const rootRoute = createRootRoute({ component: RootLayout, notFoundComponent: NotFound });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: App,
});

const roomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$roomSlug",
  beforeLoad: ({ params }) => { if (!params.roomSlug.startsWith("~")) throw notFound(); },
  component: App,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: desktopOnly,
  component: AdminPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

const premiumRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/premium",
  component: PremiumPage,
});

const myRoomsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-rooms",
  component: MyRoomsPage,
});

const roomEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-rooms/edit/$roomId",
  beforeLoad: desktopOnly,
  component: RoomEditorPage,
});

const sourceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/source/$sourceId",
  component: SourcePage,
});

const adminSourceEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/source/$sourceId/editor",
  beforeLoad: desktopOnly,
  component: SourceEditorPage,
});

const digestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/digest",
  component: DigestPage,
});

const filtersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/filters",
  component: FiltersPage,
});

const webhooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/webhooks",
  component: WebhooksPage,
});

const rulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/rules",
  component: RulesPage,
});

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/integrations",
  component: IntegrationsPage,
});

const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team",
  component: TeamPage,
});

const adminFeedsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/feeds",
  beforeLoad: desktopOnly,
  component: AdminFeedsPage,
});

const adminRoomsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/rooms",
  beforeLoad: desktopOnly,
  component: AdminRoomsPage,
});

const adminRoomEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/rooms/$roomId",
  beforeLoad: desktopOnly,
  component: AdminRoomEditorPage,
});

const adminFeedEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/feeds/$sourceId",
  beforeLoad: desktopOnly,
  component: SourceEditorPage,
});

const adminFeedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/user_feedback",
  beforeLoad: desktopOnly,
  component: AdminFeedbackPage,
});

const adminIssuesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/reported_issues",
  beforeLoad: desktopOnly,
  component: AdminIssuesPage,
});

const adminCatalogImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/catalog/import",
  beforeLoad: desktopOnly,
  component: AdminCatalogImportPage,
});

const adminCatalogReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/catalog/review",
  beforeLoad: desktopOnly,
  component: AdminCatalogReviewPage,
});

const feedEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/source/$sourceId/edit",
  beforeLoad: desktopOnly,
  component: FeedEditorPage,
});

const adminSiteMapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/sitemap",
  beforeLoad: desktopOnly,
  component: AdminSiteMapPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  roomRoute,
  adminRoute,
  profileRoute,
  premiumRoute,
  myRoomsRoute,
  roomEditorRoute,
  sourceRoute,
  adminSourceEditorRoute,
  digestRoute,
  filtersRoute,
  webhooksRoute,
  rulesRoute,
  integrationsRoute,
  teamRoute,
  adminFeedsRoute,
  adminFeedEditorRoute,
  adminRoomsRoute,
  adminRoomEditorRoute,
  adminFeedbackRoute,
  adminIssuesRoute,
  adminCatalogImportRoute,
  adminCatalogReviewRoute,
  feedEditorRoute,
  adminSiteMapRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
